import React from "react";
import { Dialog } from "@mui/material";
import { Icon } from "@iconify/react";
import { useFormikContext } from "formik";
import { cn } from "../../components/shared/Card";
import { Input, Label } from "../../components/shared/InputField";
import { Select } from "../../components/shared/Select";
import { RadioGroup, RadioGroupItem } from "../../components/shared/RadioGroup";
import { SwitchButton } from "../../components/shared/SwitchButton";
import {
  HStack,
  VStack,
  ButtonPrimary,
  ButtonPrimary1,
} from "../../components/utils";
import {
  AddPlanReq,
  ActiveEmployeeExpiry,
  EmployeeExitType,
  ExerciseWindow,
  ExerciseWindowUnit,
  ExitDateBasis,
  ExitForfeitureRule,
  ExpiryMode,
  ForfeitureSettings,
} from "../../types/EsopPlan";

// ─── Constants ────────────────────────────────────────────────────────────────

const EXIT_TYPE_LABELS: Record<EmployeeExitType, string> = {
  [EmployeeExitType.RESIGNATION]: "Resignation",
  [EmployeeExitType.TERMINATION]: "Termination",
  [EmployeeExitType.RETIREMENT]: "Retirement",
  [EmployeeExitType.PERFORMANCE_RELATED]: "Performance Related",
  [EmployeeExitType.MISCONDUCT]: "Misconduct",
  [EmployeeExitType.PERMANENT_DISABILITY]: "Permanent Disability",
  [EmployeeExitType.DEATH]: "Death",
  [EmployeeExitType.ABANDONMENT]: "Abandonment",
};

const ALL_EXIT_TYPES = Object.values(EmployeeExitType);

const UNIT_OPTIONS = [
  { value: ExerciseWindowUnit.DAYS, text: "Days" },
  { value: ExerciseWindowUnit.MONTHS, text: "Months" },
  { value: ExerciseWindowUnit.YEARS, text: "Years" },
];

const EXPIRY_MODE_OPTIONS = [
  {
    value: ExpiryMode.GRANT_EXPIRY,
    label: "Expires from grant date",
    description:
      "Options expire after a fixed duration counted from the grant date.",
    showWindow: true,
  },
  {
    value: ExpiryMode.FROM_VESTING_DATE,
    label: "Expires from vesting date",
    description:
      "Each vested lot expires after a fixed duration counted from its own vesting date.",
    showWindow: true,
  },
  {
    value: ExpiryMode.NEVER,
    label: "Never expires",
    description:
      "Vested options never expire and remain exercisable indefinitely.",
    showWindow: false,
  },
];

// ─── Helper to build a default exit rule ─────────────────────────────────────

function defaultExitRule(exitType: EmployeeExitType): ExitForfeitureRule {
  return {
    exitType,
    vestedOptions: {
      exerciseWindow: { value: 90, unit: ExerciseWindowUnit.DAYS },
      onExpiry: "FORFEIT",
    },
    unvestedOptions: {
      forfeitImmediately: true,
      gracePeriodDays: undefined,
    },
  };
}

// ─── Sub-component: Active Employee Expiry section ────────────────────────────

interface ActiveExpiryProps {
  value: ActiveEmployeeExpiry;
  onChange: (val: ActiveEmployeeExpiry) => void;
  disabled?: boolean;
}

function ActiveEmployeeExpirySection({
  value,
  onChange,
  disabled,
}: ActiveExpiryProps) {
  return (
    <VStack className="gap-4">
      <div>
        <p className="text-sm font-medium mb-1">Active Employee Expiry</p>
        <p className="text-xs text-gray-500">
          What happens to vested options while the employee is still active?
        </p>
      </div>

      <RadioGroup
        value={value.expiryMode}
        onValueChange={(mode) =>
          onChange({
            ...value,
            expiryMode: mode as ExpiryMode,
            // Clear value/unit only when switching to NEVER; preserve otherwise
            expiryValue:
              mode === ExpiryMode.NEVER ? undefined : value.expiryValue ?? 12,
            expiryUnit:
              mode === ExpiryMode.NEVER
                ? undefined
                : value.expiryUnit ?? ExerciseWindowUnit.MONTHS,
          })
        }
        disabled={disabled}
        className="space-y-3"
      >
        {EXPIRY_MODE_OPTIONS.map((opt) => (
          <label
            key={opt.value}
            htmlFor={`active-expiry-${opt.value}`}
            className={cn(
              "block cursor-pointer rounded-lg border p-4 transition-all duration-200",
              value.expiryMode === opt.value
                ? "border-orange-501 bg-orange-50/30"
                : "border-gray-200 hover:border-gray-300",
              disabled && "cursor-not-allowed"
            )}
          >
            <HStack className="items-start gap-3">
              <RadioGroupItem
                value={opt.value}
                id={`active-expiry-${opt.value}`}
                className="mt-0.5"
              />
              <VStack className="flex-1 gap-0.5">
                <span
                  className={cn(
                    "text-sm font-medium",
                    disabled && "opacity-50"
                  )}
                >
                  {opt.label}
                </span>
                <span
                  className={cn(
                    "text-xs text-gray-500",
                    disabled && "opacity-50"
                  )}
                >
                  {opt.description}
                </span>
              </VStack>
            </HStack>

            {opt.showWindow && value.expiryMode === opt.value && (
              <div
                className="mt-4 flex items-center gap-3"
                onClick={(e) => e.preventDefault()}
              >
                <div className="space-y-1">
                  <Label className="text-xs font-normal text-gray-500">
                    Duration
                  </Label>
                  <Input
                    type="number"
                    min="1"
                    disabled={disabled}
                    value={value.expiryValue ?? ""}
                    onChange={(e) =>
                      onChange({
                        ...value,
                        expiryValue:
                          e.target.value === ""
                            ? undefined
                            : Number(e.target.value),
                      })
                    }
                    className="w-28 h-9"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-normal text-gray-500">
                    Unit
                  </Label>
                  <Select
                    disabled={disabled}
                    className="w-32"
                    options={UNIT_OPTIONS}
                    valueGetter={(o) => o.value}
                    textGetter={(o) => o.text}
                    value={value.expiryUnit ?? ExerciseWindowUnit.MONTHS}
                    onChange={(e: any) =>
                      onChange({
                        ...value,
                        expiryUnit: e.target.value as ExerciseWindowUnit,
                      })
                    }
                  />
                </div>
              </div>
            )}
          </label>
        ))}
      </RadioGroup>
    </VStack>
  );
}

// ─── Sub-component: Single Exit Rule card ─────────────────────────────────────

interface ExitRuleCardProps {
  rule: ExitForfeitureRule;
  onChange: (rule: ExitForfeitureRule) => void;
  onRemove: () => void;
  onApplyToAll?: () => void;
  showApplyToAll?: boolean;
  exitDateBasis: ExitDateBasis;
  disabled?: boolean;
}

function ExitRuleCard({
  rule,
  onChange,
  onRemove,
  onApplyToAll,
  showApplyToAll,
  exitDateBasis,
  disabled,
}: ExitRuleCardProps) {
  const { vestedOptions, unvestedOptions } = rule;
  const ew = vestedOptions.exerciseWindow;

  const setVestedWindow = (patch: Partial<ExerciseWindow>) =>
    onChange({
      ...rule,
      vestedOptions: {
        ...vestedOptions,
        exerciseWindow: { ...ew, ...patch },
      },
    });

  const exitDateBasisLabel =
    exitDateBasis === ExitDateBasis.RESIGNATION_DATE
      ? "resignation date"
      : "last working date";

  const windowValue = ew.value ?? 0;
  const unitText =
    ew.unit === ExerciseWindowUnit.DAYS
      ? windowValue === 1
        ? "day"
        : "days"
      : ew.unit === ExerciseWindowUnit.MONTHS
      ? windowValue === 1
        ? "month"
        : "months"
      : windowValue === 1
      ? "year"
      : "years";

  const vestedSummary = ew.neverExpires
    ? `Vested options never expire after ${exitDateBasisLabel}.`
    : `Vested options expire ${windowValue} ${unitText} after ${exitDateBasisLabel}.`;

  return (
    <div className="rounded-lg border border-border/60 bg-muted/20 p-5 space-y-5">
      {/* Card header */}
      <HStack className="justify-between items-center">
        <span className="text-sm font-semibold">
          {EXIT_TYPE_LABELS[rule.exitType]}
        </span>
        <HStack className="items-center gap-6">
          {showApplyToAll && !disabled && (
            <button
              type="button"
              onClick={onApplyToAll}
              className="text-xs font-medium text-orange-501 hover:text-orange-501 transition-colors"
              title="Apply this rule settings to all cards"
            >
              Apply same to all Rules
            </button>
          )}
          {!disabled && (
            <button
              type="button"
              onClick={onRemove}
              className="text-gray-400 hover:text-red-500 transition-colors"
              title="Remove rule"
            >
              <Icon icon="mdi:close" width={18} />
            </button>
          )}
        </HStack>
      </HStack>

      {/* Vested Options */}
      <VStack className="gap-3">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
          Vested Options
        </p>
        <HStack className="flex-wrap gap-6">
          {/* Never expires toggle */}
          <div className="space-y-1">
            <Label className="text-xs font-normal text-gray-500">
              Never Expires
            </Label>
            <div className="pt-1">
              <SwitchButton
                label=""
                value={ew.neverExpires ?? false}
                disabled={disabled}
                onClick={() =>
                  setVestedWindow({
                    neverExpires: !ew.neverExpires,
                    value: ew.neverExpires ? ew.value || 90 : ew.value,
                  })
                }
              />
            </div>
          </div>
          {/* Exercise window value & unit */}
          {!ew.neverExpires && (
            <>
              <div className="space-y-1">
                <Label className="text-xs font-normal text-gray-500">
                  Exercise Window
                </Label>
                <Input
                  type="number"
                  min="1"
                  disabled={disabled}
                  value={ew.value ?? ""}
                  onChange={(e) =>
                    setVestedWindow({ value: Number(e.target.value) })
                  }
                  className="w-24 h-9"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-normal text-gray-500">
                  Unit
                </Label>
                <Select
                  disabled={disabled}
                  className="w-32"
                  options={UNIT_OPTIONS}
                  valueGetter={(o) => o.value}
                  textGetter={(o) => o.text}
                  value={ew.unit}
                  onChange={(e: any) =>
                    setVestedWindow({
                      unit: e.target.value as ExerciseWindowUnit,
                    })
                  }
                />
              </div>
            </>
          )}
        </HStack>
      </VStack>

      <VStack className="gap-1 rounded-md bg-white/60">
        <p className="text-xs text-gray-500">{vestedSummary}</p>
      </VStack>

      {/* Divider */}
      <div className="border-t border-dashed" />
    </div>
  );
}

// ─── Main ForfeitureSettingsForm ──────────────────────────────────────────────

interface ForfeitureSettingsFormProps {
  /** Controlled mode: provide value + onChange to use outside Formik */
  value?: ForfeitureSettings;
  onChange?: (val: ForfeitureSettings) => void;
  disabled?: boolean;
}

export function ForfeitureSettingsForm({
  value: controlledValue,
  onChange: controlledOnChange,
  disabled,
}: ForfeitureSettingsFormProps) {
  // Support both standalone (controlled) and Formik-embedded usage
  const formik = (() => {
    try {
      return useFormikContext<AddPlanReq>();
    } catch {
      return null;
    }
  })();

  const settings: ForfeitureSettings = controlledValue ??
    formik?.values.forfeitureSettings ?? {
      activeEmployee: {
        expiryMode: ExpiryMode.GRANT_EXPIRY,
      },
      exitRules: [],
    };

  const setSettings = (val: ForfeitureSettings) => {
    if (controlledOnChange) {
      controlledOnChange(val);
    } else {
      formik?.setFieldValue("forfeitureSettings", val);
    }
  };

  const applyFirstRuleToAll = (baseRule: ExitForfeitureRule) => {
    setSettings({
      ...settings,
      exitRules: settings.exitRules.map((r) => ({
        ...r,
        vestedOptions: {
          ...baseRule.vestedOptions,
          exerciseWindow: {
            ...baseRule.vestedOptions.exerciseWindow,
          },
        },
        unvestedOptions: {
          ...baseRule.unvestedOptions,
        },
      })),
    });
  };

  const addedExitTypes = new Set(settings.exitRules.map((r) => r.exitType));
  const unaddedExitTypes = ALL_EXIT_TYPES.filter((t) => !addedExitTypes.has(t));

  /** Add every remaining exit type with default values */
  const addAllRules = () => {
    setSettings({
      ...settings,
      exitRules: [
        ...settings.exitRules,
        ...unaddedExitTypes.map(defaultExitRule),
      ],
    });
  };

  const addRule = (exitType: EmployeeExitType) => {
    setSettings({
      ...settings,
      exitRules: [...settings.exitRules, defaultExitRule(exitType)],
    });
  };

  const removeRule = (exitType: EmployeeExitType) => {
    setSettings({
      ...settings,
      exitRules: settings.exitRules.filter((r) => r.exitType !== exitType),
    });
  };

  const updateRule = (
    exitType: EmployeeExitType,
    updated: ExitForfeitureRule
  ) => {
    setSettings({
      ...settings,
      exitRules: settings.exitRules.map((r) =>
        r.exitType === exitType ? updated : r
      ),
    });
  };

  return (
    <VStack className="gap-8">
      {/* ── Section 1: Active Employee Expiry ── */}
      <div className="rounded-lg border border-border p-6 space-y-4">
        <ActiveEmployeeExpirySection
          value={settings.activeEmployee}
          onChange={(activeEmployee) =>
            setSettings({ ...settings, activeEmployee })
          }
          disabled={disabled}
        />
      </div>

      {/* ── Section 2: Exit Date Basis ── */}
      <div className="rounded-lg border border-border p-6 space-y-4">
        <div>
          <p className="text-sm font-medium mb-1">Exit Date Basis</p>
          <p className="text-xs text-gray-500">
            Which date marks the start of the exercise window when an employee
            exits.
          </p>
        </div>
        <HStack className="gap-3 flex-wrap">
          {(
            [
              {
                value: ExitDateBasis.LAST_WORKING_DATE,
                label: "Last Working Date",
                description:
                  "Exercise window starts from the employee's last working day.",
              },
              {
                value: ExitDateBasis.RESIGNATION_DATE,
                label: "Resignation Date",
                description:
                  "Exercise window starts from the date the resignation was submitted.",
              },
            ] as { value: ExitDateBasis; label: string; description: string }[]
          ).map((opt) => {
            const active =
              (settings.exitDateBasis ?? ExitDateBasis.LAST_WORKING_DATE) ===
              opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                disabled={disabled}
                onClick={() =>
                  setSettings({ ...settings, exitDateBasis: opt.value })
                }
                className={cn(
                  "flex-1 min-w-[180px] text-left rounded-lg border p-4 transition-all duration-200",
                  active
                    ? "border-orange-501 bg-orange-50/30"
                    : "border-gray-200 hover:border-gray-300",
                  disabled && "cursor-not-allowed opacity-60"
                )}
              >
                <HStack className="items-start gap-2">
                  <div
                    className={cn(
                      "mt-0.5 w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0",
                      active ? "border-orange-500" : "border-gray-300"
                    )}
                  >
                    {active && (
                      <div className="w-2 h-2 rounded-full bg-orange-500" />
                    )}
                  </div>
                  <VStack className="gap-0.5">
                    <span className="text-sm font-medium">{opt.label}</span>
                    <span className="text-xs text-gray-500">
                      {opt.description}
                    </span>
                  </VStack>
                </HStack>
              </button>
            );
          })}
        </HStack>
      </div>

      {/* ── Section 3: Exit Rules ── */}
      <VStack className="gap-4">
        {/* Section header + Bulk Config trigger */}
        <HStack className="justify-between items-start">
          <div>
            <p className="text-sm font-medium mb-1">Exit Rules</p>
            <p className="text-xs text-gray-500">
              Define what happens to vested and unvested options when an
              employee exits under each separation type.
            </p>
          </div>
        </HStack>

        {/* Existing rule cards */}
        {settings.exitRules.map((rule, index) => (
          <ExitRuleCard
            key={rule.exitType}
            rule={rule}
            onChange={(updated) => updateRule(rule.exitType, updated)}
            onRemove={() => removeRule(rule.exitType)}
            showApplyToAll={index === 0 && settings.exitRules.length > 1}
            onApplyToAll={() => applyFirstRuleToAll(rule)}
            exitDateBasis={
              settings.exitDateBasis ?? ExitDateBasis.LAST_WORKING_DATE
            }
            disabled={disabled}
          />
        ))}

        {/* Add rule – chip selector for remaining exit types */}
        {!disabled && unaddedExitTypes.length > 0 && (
          <div className="rounded-lg border border-dashed border-gray-300 p-4 space-y-3">
            <p className="text-xs text-gray-500">
              {settings.exitRules.length === 0
                ? "Add a rule for each exit type you want to configure:"
                : "Add rules for more exit types:"}
            </p>
            <HStack className="justify-end"></HStack>
            <div className="flex flex-wrap gap-2">
              {unaddedExitTypes.map((exitType) => (
                <button
                  key={exitType}
                  type="button"
                  onClick={() => addRule(exitType)}
                  className="px-3 py-1.5 text-xs font-medium rounded-md border border-border
                    bg-background hover:border-orange-400 hover:text-orange-600 transition-colors"
                >
                  + {EXIT_TYPE_LABELS[exitType]}
                </button>
              ))}
            </div>
          </div>
        )}

        {!disabled && unaddedExitTypes.length > 0 && (
          <p className="text-xs text-gray-400 italic">
            Add rules for all exit types to ensure comprehensive coverage of
            exit scenarios. To add all remaining exit types at once, click the{" "}
            <button
              onClick={addAllRules}
              disabled={disabled}
              className="text-orange-500 underline"
            >
              <a>Add All</a>
            </button>{" "}
            button.
          </p>
        )}

        {!disabled && settings.exitRules.length === 0 && (
          <p className="text-xs text-gray-400 italic">
            No exit rules configured — vesting behaviour on exit will follow
            system defaults.
          </p>
        )}
      </VStack>
    </VStack>
  );
}

export default ForfeitureSettingsForm;
